import {
  endOfMonth,
  endOfWeek,
  endOfYear,
  isValid,
  parse,
  startOfMonth,
  startOfQuarter,
  endOfQuarter,
  startOfWeek,
  startOfYear,
  subDays,
  subMonths,
  subWeeks,
  subYears,
  setQuarter,
  setYear,
  min,
} from 'date-fns'

type WeekStartsOn = 0 | 1

interface DateRangeResult {
  from: Date
  to: Date
}

const MONTH_NAMES: Record<string, number> = {
  january: 0,
  february: 1,
  march: 2,
  april: 3,
  may: 4,
  june: 5,
  july: 6,
  august: 7,
  september: 8,
  october: 9,
  november: 10,
  december: 11,
  jan: 0,
  feb: 1,
  mar: 2,
  apr: 3,
  jun: 5,
  jul: 6,
  aug: 7,
  sep: 8,
  oct: 9,
  nov: 10,
  dec: 11,
}

const DATE_PARSE_FORMATS = ['MMM d, yyyy', 'M/d/yyyy', 'yyyy-MM-dd']

function clampTo(date: Date, maxDate: Date): Date {
  return min([date, maxDate])
}

/**
 * A range whose start is already past `maxDate` (e.g. "Q3" asked for while
 * still in Q1) has no sensible clamp — clamping only `to` would invert it
 * (start after end). Treat it as unparseable instead of returning a
 * backwards range.
 */
function clampRange(
  from: Date,
  to: Date,
  maxDate: Date,
): DateRangeResult | null {
  if (from > maxDate) return null
  return { from, to: clampTo(to, maxDate) }
}

/**
 * Parses a natural language query into a date range.
 *
 * Supported patterns:
 * - "last/past N days/weeks/months" → relative range ending today
 * - "this week/month/year" → period-to-date
 * - "last week/month/year" → previous full period
 * - "MTD", "month to date" → month-to-date
 * - "YTD", "year to date" → year-to-date
 * - "Q1 2026", "Q3" → quarter range
 * - "H1 2026", "H2" → half-year range (H1 = Jan–Jun, H2 = Jul–Dec)
 * - "January 2026", "Feb" → full month
 * - Single date fallback (same formats as footer inputs)
 *
 * Returns null if the query can't be parsed.
 */
export function parseDateRangeQuery(
  query: string,
  maxDate?: Date,
  weekStartsOn: WeekStartsOn = 0,
): DateRangeResult | null {
  const now = maxDate ?? new Date()
  const input = query.trim().toLowerCase()

  if (!input) return null

  const weekOptions = { weekStartsOn }

  // "last/past N days/weeks/months"
  const relativeMatch = input.match(
    /^(?:last|past)\s+(\d+)\s+(day|week|month|year)s?$/,
  )
  if (relativeMatch) {
    const n = parseInt(relativeMatch[1], 10)
    const unit = relativeMatch[2]
    let from: Date
    switch (unit) {
      case 'day':
        from = subDays(now, n)
        break
      case 'week':
        from = subWeeks(now, n)
        break
      case 'month':
        from = subMonths(now, n)
        break
      case 'year':
        from = subYears(now, n)
        break
      default:
        return null
    }
    return { from, to: clampTo(now, now) }
  }

  // "this week"
  if (input === 'this week') {
    return {
      from: startOfWeek(now, weekOptions),
      to: clampTo(now, now),
    }
  }

  // "last week"
  if (input === 'last week') {
    const lastWeek = subWeeks(now, 1)
    return {
      from: startOfWeek(lastWeek, weekOptions),
      to: clampTo(endOfWeek(lastWeek, weekOptions), now),
    }
  }

  // "this month"
  if (input === 'this month') {
    return {
      from: startOfMonth(now),
      to: clampTo(now, now),
    }
  }

  // "last month"
  if (input === 'last month') {
    const lastMonth = subMonths(now, 1)
    return {
      from: startOfMonth(lastMonth),
      to: clampTo(endOfMonth(lastMonth), now),
    }
  }

  // "month to date" / "mtd"
  if (input === 'month to date' || input === 'mtd') {
    return {
      from: startOfMonth(now),
      to: clampTo(now, now),
    }
  }

  // "year to date" / "ytd"
  if (input === 'year to date' || input === 'ytd') {
    return {
      from: startOfYear(now),
      to: clampTo(now, now),
    }
  }

  // "this year"
  if (input === 'this year') {
    return {
      from: startOfYear(now),
      to: clampTo(now, now),
    }
  }

  // "last year"
  if (input === 'last year') {
    const lastYear = subYears(now, 1)
    return {
      from: startOfYear(lastYear),
      to: clampTo(endOfYear(lastYear), now),
    }
  }

  // "Q1 2026", "Q3", "q2 2025"
  const quarterMatch = input.match(/^q([1-4])(?:\s+(\d{4}))?$/)
  if (quarterMatch) {
    const quarterValue = parseInt(quarterMatch[1], 10)
    const quarter =
      quarterValue === 1 ||
      quarterValue === 2 ||
      quarterValue === 3 ||
      quarterValue === 4
        ? quarterValue
        : null
    if (quarter == null) return null
    const year = quarterMatch[2]
      ? parseInt(quarterMatch[2], 10)
      : now.getFullYear()
    let date = setYear(now, year)
    date = setQuarter(date, quarter)
    return clampRange(startOfQuarter(date), endOfQuarter(date), now)
  }

  // "H1 2026", "H2", "h1 2025" — half-year (H1 = Jan–Jun, H2 = Jul–Dec)
  const halfYearMatch = input.match(/^h([12])(?:\s+(\d{4}))?$/)
  if (halfYearMatch) {
    const half = parseInt(halfYearMatch[1], 10)
    const year = halfYearMatch[2]
      ? parseInt(halfYearMatch[2], 10)
      : now.getFullYear()
    const startMonth = half === 1 ? 0 : 6
    const endMonth = half === 1 ? 5 : 11
    return clampRange(
      startOfMonth(new Date(year, startMonth, 1)),
      endOfMonth(new Date(year, endMonth, 1)),
      now,
    )
  }

  // "April 2026 - May 2026", "April to May 2026", "April - May 2026"
  const monthRangeMatch = input.match(
    /^([a-z]+)(?:\s+(\d{4}))?\s*(?:-|–|—|\bto\b)\s*([a-z]+)(?:\s+(\d{4}))?$/,
  )
  if (monthRangeMatch) {
    const startIdx = MONTH_NAMES[monthRangeMatch[1]]
    const endIdx = MONTH_NAMES[monthRangeMatch[3]]
    if (startIdx !== undefined && endIdx !== undefined) {
      const y1 = monthRangeMatch[2]
      const y2 = monthRangeMatch[4]
      // Year given on one side applies to both; default to current year if neither.
      const startYear = y1 ? +y1 : y2 ? +y2 : now.getFullYear()
      const endYear = y2 ? +y2 : y1 ? +y1 : now.getFullYear()
      const from = startOfMonth(new Date(startYear, startIdx, 1))
      const to = endOfMonth(new Date(endYear, endIdx, 1))
      // Reversed range is treated as unparseable rather than guessing intent.
      if (from <= to) return clampRange(from, to, now)
    }
    // Non-month words or reversed range — fall through to null
  }

  // "January 2026", "Feb", "march 2025"
  const monthNameMatch = input.match(/^([a-z]+)(?:\s+(\d{4}))?$/)
  if (monthNameMatch) {
    const monthName = monthNameMatch[1]
    const monthIndex = MONTH_NAMES[monthName]
    if (monthIndex !== undefined) {
      const year = monthNameMatch[2]
        ? parseInt(monthNameMatch[2], 10)
        : now.getFullYear()
      const date = new Date(year, monthIndex, 1)
      return clampRange(startOfMonth(date), endOfMonth(date), now)
    }
  }

  // Single date fallback — try the same formats as the footer inputs
  const ref = new Date()
  for (const fmt of DATE_PARSE_FORMATS) {
    const parsed = parse(input, fmt, ref)
    if (isValid(parsed)) {
      if (parsed.getFullYear() < 100) {
        const corrected = parsed.getFullYear() + 2000
        if (Math.abs(corrected - now.getFullYear()) > 10) continue
        parsed.setFullYear(corrected)
      }
      const clamped = clampTo(parsed, now)
      return { from: clamped, to: clamped }
    }
  }

  return null
}
